import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Tooltip,
  Button,
  Tag,
  Select,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RiAddBoxFill } from "react-icons/ri";
import AddsourceModal from "./Add";
import EditsourceModal from "./Edit";
import { hasPermission } from "../../utils/HasPermission";
import { getAllSources, deleteSource } from "../../utils/API";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Source {
  _id: string;
  name: string;
  color: string;
  slug: string;
  category: string | string[];
  isActive: boolean;
}

const SourcePage = () => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState<Source[]>([]);
  const [filteredData, setFilteredData] = useState<Source[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState<Source | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch sources from the API
  const getSource = async () => {
    setLoading(true);
    try {
      const response: any = await getAllSources(
        searchText,
        page,
        selectedCategories,
      );
      console.log(
        "Response structure check:",
        JSON.stringify(response, null, 2),
      );

      const sourceList = response?.sources || [];
      let flatList: Source[] = [];

      const flattenSources = (sources: any[]) => {
        for (const src of sources) {
          flatList.push({
            _id: src._id,
            name: src.name,
            slug: src.slug,
            category: src.category,
            isActive: src.isActive ?? true,
            color: src.color || "#000000",
          });

          if (Array.isArray(src.sources)) {
            flattenSources(src.sources);
          }
        }
      };

      if (Array.isArray(sourceList)) {
        flattenSources(sourceList);

        const sortedData = flatList
          .sort((a, b) => (b._id > a._id ? 1 : -1))
          .map((source, index) => ({
            ...source,
            serialNumber: index + 1,
          }));

        setDataSource(sortedData);
        setFilteredData(sortedData);
        setTotal(response.total || 0);

        // Extract unique categories for the Select options
        const uniqueCategories = Array.from(
          new Set(
            flatList
              .flatMap((source) =>
                Array.isArray(source.category)
                  ? source.category
                  : [source.category],
              )
              .filter((cat): cat is string => !!cat),
          ),
        );
        setCategoryOptions(uniqueCategories);
      } else {
        console.log("No valid source list found");
        setDataSource([]);
        setFilteredData([]);
        setCategoryOptions([]);
      }
    } catch (error) {
      message.error("Network error. Please try again.");
      setDataSource([]);
      setFilteredData([]);
      setCategoryOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on page, searchText, or selectedCategories change
  useEffect(() => {
    getSource();
  }, [page, searchText, selectedCategories]);

  // Apply client-side filtering
  useEffect(() => {
    const filtered = dataSource.filter((source) => {
      const searchValue = searchText.toLowerCase();

      const nameMatch = source.name?.toLowerCase().includes(searchValue);

      const categoryMatch = Array.isArray(source.category)
        ? source.category.some((cat) =>
            cat?.toLowerCase().includes(searchValue),
          )
        : source.category?.toLowerCase().includes(searchValue);

      const categoryFilterMatch =
        selectedCategories.length === 0 ||
        (Array.isArray(source.category)
          ? selectedCategories.some((cat) => source.category.includes(cat))
          : selectedCategories.includes(source.category));

      return (nameMatch || categoryMatch) && categoryFilterMatch;
    });

    setFilteredData(filtered);
  }, [searchText, dataSource, selectedCategories]);

  // Handle category selection
  const handleCategoryChange = (value: string[]) => {
    setSelectedCategories(value);
    setPage(1); // Reset page to 1 when category filter changes
  };

  const handleEdit = async (record: Source) => {
    if (!record._id) {
      message.error("Invalid source ID");
      return;
    }
    setCurrentTask(record);
    setIsEditModalVisible(true);
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response: any = await deleteSource(deleteId);
      if (response) {
        message.success("Source deleted successfully");
        setFilteredData((prevData) =>
          prevData.filter((item) => item._id !== deleteId),
        );
        setDataSource((prevData) =>
          prevData.filter((item) => item._id !== deleteId),
        );
        setDeleteId(null);
      }
    } catch (error) {
      message.error("Error deleting source. Please try again.");
    }
  };

  // convert hex to RGBA with given alpha
  const hexToRGBA = (hex: string, alpha = 0.15) => {
    let r = 0,
      g = 0,
      b = 0;

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      render: (_: any, __: Source, index: number) => (
        <span>{(page - 1) * 10 + index + 1}</span>
      ),
    },
    {
      title: "Source Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Source) => (
        <Tag
          style={{
            backgroundColor: hexToRGBA(record.color || "#000000", 0.15), // light background
            color: record.color, // text stays original color
            borderRadius: "6px",
            borderColor: record.color,
            fontWeight: 600,
            display: "inline-block",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Source Slug",
      dataIndex: "slug",
      key: "slug",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (categories: string | string[]) => {
        if (!categories) return null;
        const cats = Array.isArray(categories) ? categories : [categories];
        return (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {cats.map((cat) => (
              <Tag
                key={cat}
                color="#24a2dcff"
                style={{
                  fontWeight: "bold",
                  borderRadius: "12px",
                  padding: "0 8px",
                }}
              >
                {cat}
              </Tag>
            ))}
          </div>
        );
      },
    },
    ...(hasPermission("designations", "update") ||
    hasPermission("designations", "delete")
      ? [
          {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_: any, record: Source) => (
              <div
                className="d-flex"
                style={{ gap: "8px", alignItems: "center" }}
              >
                {hasPermission("designations", "update") && (
                  <Tooltip title="Edit">
                    <button
                      onClick={() => handleEdit(record)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                            shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                            hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                            active:translate-y-[1px] active:shadow-none 
                            text-white flex items-center justify-center 
                            transition-all duration-200 ease-in-out group"
                    >
                      <FaPen
                        className="group-hover:scale-110 transition-transform duration-200"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}
                {hasPermission("designations", "delete") && (
                  <Tooltip title="Delete">
                    <button
                      onClick={() => handleDeleteConfirmation(record._id)}
                      className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                            shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                            hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                            active:translate-y-1 active:shadow-none 
                            text-white flex items-center justify-center 
                            transition-all duration-200 group"
                    >
                      <FaTrashAlt
                        className="group-hover:scale-110 transition-transform"
                        size={12}
                      />
                    </button>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 className="headername">Source</h2>
        </Col>
        <Col xs={24} md={10}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Select
              mode="multiple"
              placeholder="Filter by Category"
              style={{ width: "250px" }}
              optionLabelProp="label"
              listHeight={200}
              value={selectedCategories}
              onChange={handleCategoryChange}
              allowClear
            >
              {categoryOptions.map((category) => (
                <Select.Option key={category} value={category} label={category}>
                  {category}
                </Select.Option>
              ))}
            </Select>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search..."
              allowClear
              value={searchText}
              style={{ width: 200 }}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1); // Reset page on search
              }}
            />
            <Button
              type="primary"
              onClick={() => setIsModalOpen(true)}
              className="allbuttons flex items-center"
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>
            <AddsourceModal
              visible={isModalOpen}
              onTaskCreated={() => {
                getSource();
                setIsModalOpen(false);
              }}
              onClose={() => setIsModalOpen(false)}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Card className="w-100">
          <Col>
            <Spin spinning={loading}>
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  current: page,
                  total: total,
                  onChange: (pageNumber) => setPage(pageNumber),
                  showSizeChanger: false,
                }}
              />
            </Spin>
          </Col>
        </Card>
      </Row>
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Source?</p>
      </Modal>
      {isEditModalVisible && (
        <EditsourceModal
          visible={isEditModalVisible}
          taskId={currentTask?._id}
          onClose={() => {
            setIsEditModalVisible(false);
            setCurrentTask(null);
          }}
          onSuccess={() => {
            setIsEditModalVisible(false);
            setCurrentTask(null);
            getSource();
          }}
        />
      )}
    </div>
  );
};

export const SOURCE_TABLE_COLUMNS = ["name", "slug", "category"];

export default SourcePage;
